import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calculator, Video, Users, Target, Settings, BarChart3, Award, TrendingUp, DollarSign, Leaf, Globe, BookOpen, Zap, Phone, Monitor, ArrowRight, Play, Star, FileText, PieChart, LineChart, Table, Receipt, Percent, Calendar, Shield, Coins, TreeDeciduous, Sprout, Trees, GraduationCap, Rocket, CheckCircle2 } from 'lucide-react';
import { lazy, Suspense } from 'react';

const ReinvestmentCalculator = lazy(() => import('./ReinvestmentCalculator'));
const SimplifiedCalculator = lazy(() => import('./SimplifiedCalculator'));
const ExpressCalculator = lazy(() => import('./ExpressCalculator'));
const MultiplierTreeCalculator = lazy(() => import('./MultiplierTreeCalculator'));
const RetirementCalculator = lazy(() => import('./RetirementCalculator'));
const SegubecaCalculator = lazy(() => import('./SegubecaCalculator'));
const ICMLandingPage = lazy(() => import('./ICMLandingPage'));
const PassiveIncomeCalculator = lazy(() => import('./PassiveIncomeCalculator'));

const CalculatorLoading = () => (
  <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-green-200 border-t-green-600 rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600 font-medium">Cargando calculadora...</p>
      <p className="text-gray-500 text-sm mt-2">Preparando tu experiencia financiera</p>
    </div>
  </div>
);

interface HomePageProps {
  className?: string;
}

const HomePage: React.FC<HomePageProps> = ({ className = '' }) => {
  const [selectedCalculator, setSelectedCalculator] = useState<'home' | 'full' | 'simplified' | 'express' | 'tree' | 'retirement' | 'segubeca' | 'icm' | 'passive'>('home');

  if (selectedCalculator === 'full') {
    return (
      <Suspense fallback={<CalculatorLoading />}>
        <ReinvestmentCalculator onBack={() => setSelectedCalculator('home')} />
      </Suspense>
    );
  }

  if (selectedCalculator === 'simplified') {
    return (
      <Suspense fallback={<CalculatorLoading />}>
        <SimplifiedCalculator onBack={() => setSelectedCalculator('home')} />
      </Suspense>
    );
  }

  if (selectedCalculator === 'express') {
    return (
      <Suspense fallback={<CalculatorLoading />}>
        <ExpressCalculator onBack={() => setSelectedCalculator('home')} />
      </Suspense>
    );
  }

  if (selectedCalculator === 'tree') {
    return (
      <Suspense fallback={<CalculatorLoading />}>
        <MultiplierTreeCalculator onBack={() => setSelectedCalculator('home')} />
      </Suspense>
    );
  }

  if (selectedCalculator === 'retirement') {
    return (
      <Suspense fallback={<CalculatorLoading />}>
        <RetirementCalculator onBack={() => setSelectedCalculator('home')} />
      </Suspense>
    );
  }

  if (selectedCalculator === 'segubeca') {
    return (
      <Suspense fallback={<CalculatorLoading />}>
        <SegubecaCalculator onBack={() => setSelectedCalculator('home')} />
      </Suspense>
    );
  }

  if (selectedCalculator === 'icm') {
    return (
      <Suspense fallback={<CalculatorLoading />}>
        <ICMLandingPage onGetStarted={() => setSelectedCalculator('full')} />
      </Suspense>
    );
  }

  if (selectedCalculator === 'passive') {
    return (
      <Suspense fallback={<CalculatorLoading />}>
        <PassiveIncomeCalculator onBack={() => setSelectedCalculator('home')} />
      </Suspense>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 ${className}`}>
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-20">
            <div className="flex items-center gap-6">
              <img
                src="https://storage.googleapis.com/msgsndr/UGHEqDP73MnMMls2nRHK/media/68ca09ef7d860663deeedd6a.png"
                alt="Cosecha Capital"
                className="h-20 w-auto drop-shadow-lg"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Certificado de Crecimiento Exponencial
                </h1>
                <p className="text-gray-600">
                  Suite de Calculadoras para el Interés Compuesto Multiplicador™
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center gap-8 mb-8">
            <img
              src="https://storage.googleapis.com/msgsndr/UGHEqDP73MnMMls2nRHK/media/68ca09ef7d860663deeedd6a.png"
              alt="Cosecha Capital"
              className="h-32 w-auto drop-shadow-2xl"
            />
            <div className="w-24 h-24 bg-gradient-to-br from-green-500 to-emerald-600 rounded-3xl flex items-center justify-center shadow-2xl">
              <Zap className="w-12 h-12 text-white" />
            </div>
          </div>

          <h2 className="text-5xl font-bold text-gray-900 mb-6">
            INTERÉS COMPUESTO MULTIPLICADOR™
          </h2>

          <p className="text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
            No solo multiplica dinero, multiplica activos reales productivos
          </p>

          <div className="bg-white rounded-2xl shadow-lg p-8 max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Target className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Activos Reales</h3>
                <p className="text-gray-600">Inversión respaldada por plantaciones productivas de limón</p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Crecimiento Exponencial</h3>
                <p className="text-gray-600">Las utilidades se reinvierten para adquirir más certificados</p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <DollarSign className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Ingresos Dolarizados</h3>
                <p className="text-gray-600">Protección natural contra devaluación por exportación</p>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="space-y-8"
        >
          <div className="text-center">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">
              Elige tu Calculadora
            </h3>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Tres experiencias optimizadas para diferentes necesidades profesionales
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8 max-w-7xl mx-auto">
            <motion.div
              whileHover={{ scale: 1.02, y: -5 }}
              whileTap={{ scale: 0.98 }}
              className="bg-white rounded-3xl shadow-xl overflow-hidden cursor-pointer group"
              onClick={() => setSelectedCalculator('full')}
            >
              <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-8 text-white">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                    <Calculator className="w-8 h-8" />
                  </div>
                  <div>
                    <h4 className="text-2xl font-bold">Calculadora Completa</h4>
                    <p className="text-blue-100">Experiencia educativa integral</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-blue-100">
                  <Monitor className="w-5 h-5" />
                  <span>Para presentaciones profesionales</span>
                </div>
              </div>

              <div className="p-8">
                <div className="space-y-6">
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <Star className="w-5 h-5 text-blue-500" />
                      <span>11 Pasos Educativos</span>
                    </h5>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-start gap-2">
                        <Globe className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Idioma y Moneda:</strong> MXN, USD, EUR, personalizable</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <BookOpen className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Educación:</strong> 8 conceptos interactivos del ICM™</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Target className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Metas:</strong> Patrimonio, ingreso pasivo, tiempo</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Shield className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Protección Legal:</strong> Trust, voluntad anticipada</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Users className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Cliente:</strong> Datos completos y beneficiarios</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Coins className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Inversión:</strong> Certificados, precios, reinversión</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <LineChart className="w-4 h-4 text-cyan-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Análisis de Mercado:</strong> Escenarios múltiples</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Calendar className="w-4 h-4 text-pink-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Plan de Retiros:</strong> Calendario personalizado</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Leaf className="w-4 h-4 text-lime-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Impacto:</strong> Análisis sostenible ESG</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <BarChart3 className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Resultados:</strong> 15+ gráficos, tablas, métricas</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-5 h-5 text-blue-600" />
                      <span className="font-medium text-blue-800">5 Reportes Especializados</span>
                    </div>
                    <div className="text-xs text-blue-700 space-y-1">
                      <p>• Evolución de Certificados (detallado)</p>
                      <p>• Multiplicador Compuesto (educativo)</p>
                      <p>• Análisis Comparativo (vs alternativas)</p>
                      <p>• Análisis Completo (presentación)</p>
                      <p>• Reporte Patrimonial Existente</p>
                    </div>
                  </div>

                  <div className="bg-green-50 p-4 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Receipt className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-800">Plan de Financiamiento</span>
                    </div>
                    <div className="text-xs text-green-700">
                      Tabla de amortización, descuentos por tasa preferencial,
                      cálculo automático con opciones editables de enganche e interés.
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="text-sm text-gray-500">
                      ⏱️ 15-20 minutos
                    </div>
                    <div className="flex items-center gap-2 text-blue-600 group-hover:text-blue-700 transition-colors">
                      <span className="font-medium">Iniciar</span>
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02, y: -5 }}
              whileTap={{ scale: 0.98 }}
              className="bg-white rounded-3xl shadow-xl overflow-hidden cursor-pointer group"
              onClick={() => setSelectedCalculator('simplified')}
            >
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-8 text-white">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                    <Video className="w-8 h-8" />
                  </div>
                  <div>
                    <h4 className="text-2xl font-bold">Calculadora Simplificada</h4>
                    <p className="text-green-100">Optimizada para videollamadas</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-green-100">
                  <Phone className="w-5 h-5" />
                  <span>Ideal para asesorías en vivo</span>
                </div>
              </div>

              <div className="p-8">
                <div className="space-y-6">
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <Zap className="w-5 h-5 text-green-500" />
                      <span>7 Pasos Esenciales</span>
                    </h5>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-start gap-2">
                        <Globe className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Configuración:</strong> Idioma y moneda rápida</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Target className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Metas:</strong> Objetivos del inversionista</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Coins className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Datos Específicos:</strong> Inversión y reinversión</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Calendar className="w-4 h-4 text-pink-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Retiros:</strong> Plan de distribución simple</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Users className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Cliente:</strong> Información básica</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <BarChart3 className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Resultados:</strong> Gráficos clave y métricas</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-green-50 p-4 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-800">Mismos 5 Reportes</span>
                    </div>
                    <div className="text-xs text-green-700">
                      Acceso completo a todos los reportes especializados en HTML y PDF,
                      con plan de financiamiento incluido.
                    </div>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Play className="w-5 h-5 text-blue-600" />
                      <span className="font-medium text-blue-800">Proceso Ágil</span>
                    </div>
                    <div className="text-xs text-blue-700">
                      Sin módulos educativos ni análisis de mercado. Directo al punto
                      para consultas express en videollamada.
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="text-sm text-gray-500">
                      ⏱️ 3-5 minutos
                    </div>
                    <div className="flex items-center gap-2 text-green-600 group-hover:text-green-700 transition-colors">
                      <span className="font-medium">Iniciar</span>
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02, y: -5 }}
              whileTap={{ scale: 0.98 }}
              className="bg-white rounded-3xl shadow-xl overflow-hidden cursor-pointer group"
              onClick={() => setSelectedCalculator('tree')}
            >
              <div className="bg-gradient-to-r from-emerald-500 to-green-600 p-8 text-white">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                    <TreeDeciduous className="w-8 h-8" />
                  </div>
                  <div>
                    <h4 className="text-2xl font-bold">Árbol Multiplicador</h4>
                    <p className="text-emerald-100">Educa sin fricción</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-emerald-100">
                  <Sprout className="w-5 h-5" />
                  <span>Visualiza la diferencia: Ahorro vs ICT vs ICM</span>
                </div>
              </div>

              <div className="p-8">
                <div className="space-y-6">
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <TreeDeciduous className="w-5 h-5 text-emerald-500" />
                      <span>Analogía Sencilla</span>
                    </h5>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-start gap-2">
                        <Sprout className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Semilla:</strong> Ahorro sin intereses</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <TreeDeciduous className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Árbol:</strong> ICT sin reinvertir (0%)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Trees className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Bosque:</strong> ICM reinvierte 100%</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-emerald-50 p-4 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Play className="w-5 h-5 text-emerald-600" />
                      <span className="font-medium text-emerald-800">Vista Única Interactiva</span>
                    </div>
                    <div className="text-xs text-emerald-700">
                      Compara los 3 escenarios en una sola pantalla con animaciones y gráficos
                      que muestran el poder del efecto multiplicador.
                    </div>
                  </div>

                  <div className="bg-sky-50 p-4 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Calculator className="w-5 h-5 text-sky-600" />
                      <span className="font-medium text-sky-800">Configuración Simple</span>
                    </div>
                    <div className="text-xs text-sky-700">
                      Ajusta inversión inicial, aportaciones, plazo y tasa.
                      Los resultados se actualizan en tiempo real.
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="text-sm text-gray-500">
                      ⏱️ 2-3 minutos
                    </div>
                    <div className="flex items-center gap-2 text-emerald-600 group-hover:text-emerald-700 transition-colors">
                      <span className="font-medium">Ver Diferencia</span>
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02, y: -5 }}
              whileTap={{ scale: 0.98 }}
              className="bg-white rounded-3xl shadow-xl overflow-hidden cursor-pointer group"
              onClick={() => setSelectedCalculator('icm')}
            >
              <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-8 text-white">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                    <Rocket className="w-8 h-8" />
                  </div>
                  <div>
                    <h4 className="text-2xl font-bold">Landing ICM</h4>
                    <p className="text-purple-100">Multiplica tus rendimientos</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-purple-100">
                  <Zap className="w-5 h-5" />
                  <span>La página que vende tu inversión</span>
                </div>
              </div>

              <div className="p-8">
                <div className="space-y-6">
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-purple-500" />
                      <span>Página de Ventas Completa</span>
                    </h5>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-start gap-2">
                        <Zap className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Hero Section:</strong> Título impactante + CTA</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Calculator className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Calculadora Interactiva:</strong> Resultados en vivo</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <BarChart3 className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Gráficos Comparativos:</strong> Con vs Sin ICM</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Award className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                        <span><strong>4 Beneficios Clave:</strong> Con estadísticas</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Target className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Hitos de Crecimiento:</strong> Años 3, 5 y 10</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Users className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Testimonios:</strong> 3 casos reales</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Table className="w-4 h-4 text-cyan-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Tabla Comparativa:</strong> ICM vs Tradicional</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-purple-50 p-4 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Rocket className="w-5 h-5 text-purple-600" />
                      <span className="font-medium text-purple-800">Diseñada para Convertir</span>
                    </div>
                    <div className="text-xs text-purple-700">
                      Números reales, gráficos impactantes y CTAs estratégicos para
                      que los inversionistas tomen acción inmediata.
                    </div>
                  </div>

                  <div className="bg-pink-50 p-4 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Play className="w-5 h-5 text-pink-600" />
                      <span className="font-medium text-pink-800">Animaciones Persuasivas</span>
                    </div>
                    <div className="text-xs text-pink-700">
                      Visualiza el crecimiento exponencial con animaciones año por año
                      que demuestran el poder del ICM.
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="text-sm text-gray-500">
                      💰 Enfoque en ventas
                    </div>
                    <div className="flex items-center gap-2 text-purple-600 group-hover:text-purple-700 transition-colors">
                      <span className="font-medium">Ver Landing</span>
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02, y: -5 }}
              whileTap={{ scale: 0.98 }}
              className="bg-white rounded-3xl shadow-xl overflow-hidden cursor-pointer group"
              onClick={() => setSelectedCalculator('passive')}
            >
              <div className="bg-gradient-to-r from-green-600 to-emerald-700 p-8 text-white">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                    <Coins className="w-8 h-8" />
                  </div>
                  <div>
                    <h4 className="text-2xl font-bold">Ingreso Pasivo</h4>
                    <p className="text-green-100">Flujo mensual inteligente</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-green-100">
                  <TrendingUp className="w-5 h-5" />
                  <span>18%+ con respaldo en USD</span>
                </div>
              </div>

              <div className="p-8">
                <div className="space-y-6">
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-green-500" />
                      <span>Landing Comparativa Total</span>
                    </h5>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-start gap-2">
                        <Zap className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Hero:</strong> Ingreso que no se detiene</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <BarChart3 className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Comparativa:</strong> vs Segubeca, Renta, CETES</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Award className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Ventaja %:</strong> +192% a +1,360% según años</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Coins className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Flujo Mensual:</strong> Proyección dinámica</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Shield className="w-4 h-4 text-cyan-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Seguridad:</strong> 3 fideicomisos + 2 seguros</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <LineChart className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Gráficos:</strong> Patrimonio y flujo animados</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-green-50 p-4 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-800">100% Pasivo</span>
                    </div>
                    <div className="text-xs text-green-700">
                      A diferencia de rentas (gestión activa) o segubecas (sin flujo mensual),
                      Citrus ofrece ingreso mensual 12/12 sin que muevas un dedo.
                    </div>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Globe className="w-5 h-5 text-blue-600" />
                      <span className="font-medium text-blue-800">Exposición USD</span>
                    </div>
                    <div className="text-xs text-blue-700">
                      Utilidades con equivalencia en dólares gracias a la exportación.
                      Protección natural contra inflación.
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="text-sm text-gray-500">
                      💰 Enfoque en seguridad
                    </div>
                    <div className="flex items-center gap-2 text-green-600 group-hover:text-green-700 transition-colors">
                      <span className="font-medium">Ver Calculadora</span>
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02, y: -5 }}
              whileTap={{ scale: 0.98 }}
              className="bg-white rounded-3xl shadow-xl overflow-hidden cursor-pointer group"
              onClick={() => setSelectedCalculator('retirement')}
            >
              <div className="bg-gradient-to-r from-amber-500 to-orange-600 p-8 text-white">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                    <Target className="w-8 h-8" />
                  </div>
                  <div>
                    <h4 className="text-2xl font-bold">Calculadora de Retiro</h4>
                    <p className="text-amber-100">Planea tu futuro financiero</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-amber-100">
                  <Award className="w-5 h-5" />
                  <span>Compara AFORE, PPR y CosechaCAPITAL</span>
                </div>
              </div>

              <div className="p-8">
                <div className="space-y-6">
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <Target className="w-5 h-5 text-amber-500" />
                      <span>Simulación Interactiva</span>
                    </h5>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-start gap-2">
                        <Users className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Edad y Horizonte:</strong> Desde 25 hasta 70 años</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <DollarSign className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Inversión:</strong> Inicial + aportaciones mensuales</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <BarChart3 className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Comparación 3 vías:</strong> AFORE, PPR, CosechaCAPITAL</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Play className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Animación:</strong> Crecimiento año por año</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Table className="w-4 h-4 text-cyan-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Tabla Detallada:</strong> Evolución completa</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Award className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Resultados:</strong> Patrimonio e ingreso mensual</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-amber-50 p-4 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="w-5 h-5 text-amber-600" />
                      <span className="font-medium text-amber-800">Decisión Informada</span>
                    </div>
                    <div className="text-xs text-amber-700">
                      Visualiza cómo diferentes instrumentos de ahorro afectan tu retiro.
                      Compara rendimientos, protección contra inflación y patrimonio final.
                    </div>
                  </div>

                  <div className="bg-green-50 p-4 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-800">Ventaja CosechaCAPITAL</span>
                    </div>
                    <div className="text-xs text-green-700">
                      Descubre por qué invertir en activos productivos supera
                      significativamente las opciones tradicionales de retiro.
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="text-sm text-gray-500">
                      ⏱️ 5-7 minutos
                    </div>
                    <div className="flex items-center gap-2 text-amber-600 group-hover:text-amber-700 transition-colors">
                      <span className="font-medium">Simular Retiro</span>
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02, y: -5 }}
              whileTap={{ scale: 0.98 }}
              className="bg-white rounded-3xl shadow-xl overflow-hidden cursor-pointer group"
              onClick={() => setSelectedCalculator('segubeca')}
            >
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-8 text-white">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                    <GraduationCap className="w-8 h-8" />
                  </div>
                  <div>
                    <h4 className="text-2xl font-bold">Calculadora de Segubeca</h4>
                    <p className="text-green-100">Inversión educativa superior</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-green-100">
                  <TrendingUp className="w-5 h-5" />
                  <span>Rendimiento continuo post-educativo</span>
                </div>
              </div>

              <div className="p-8">
                <div className="space-y-6">
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <GraduationCap className="w-5 h-5 text-green-500" />
                      <span>Planeación Educativa</span>
                    </h5>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-start gap-2">
                        <Calendar className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Horizonte:</strong> Desde preescolar hasta posgrado</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <DollarSign className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Rendimiento:</strong> 18-32% anual proyectado</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <BarChart3 className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Comparación:</strong> Tradicional (3%) vs Internacional (5%) vs CosechaCAPITAL</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <TrendingUp className="w-4 h-4 text-cyan-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Beneficio único:</strong> Ingresos después del pago educativo</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Shield className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Respaldo:</strong> Tierra titulada y activos productivos</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-green-50 p-4 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Award className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-800">Ventaja Diferenciadora</span>
                    </div>
                    <div className="text-xs text-green-700">
                      A diferencia de las Segubecas tradicionales (3%) e internacionales (5%),
                      CosechaCAPITAL ofrece 18-32% con distribuciones continuas después del periodo educativo.
                    </div>
                  </div>

                  <div className="bg-emerald-50 p-4 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Leaf className="w-5 h-5 text-emerald-600" />
                      <span className="font-medium text-emerald-800">Ingresos Post-Educación</span>
                    </div>
                    <div className="text-xs text-emerald-700">
                      Tu inversión sigue generando ingresos pasivos que tus hijos pueden usar
                      para su vida profesional, a diferencia de las Segubecas que terminan.
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="text-sm text-gray-500">
                      ⏱️ 3-5 minutos
                    </div>
                    <div className="flex items-center gap-2 text-green-600 group-hover:text-green-700 transition-colors">
                      <span className="font-medium">Calcular Segubeca</span>
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02, y: -5 }}
              whileTap={{ scale: 0.98 }}
              className="bg-white rounded-3xl shadow-xl overflow-hidden cursor-pointer group"
              onClick={() => setSelectedCalculator('express')}
            >
              <div className="bg-gradient-to-r from-cyan-500 to-blue-600 p-8 text-white">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                    <Zap className="w-8 h-8" />
                  </div>
                  <div>
                    <h4 className="text-2xl font-bold">Calculadora Express</h4>
                    <p className="text-cyan-100">Todo en tiempo real</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 text-cyan-100">
                  <TrendingUp className="w-5 h-5" />
                  <span>Dashboard interactivo instantáneo</span>
                </div>
              </div>

              <div className="p-8">
                <div className="space-y-6">
                  <div>
                    <h5 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                      <Monitor className="w-5 h-5 text-cyan-500" />
                      <span>Una Sola Pantalla</span>
                    </h5>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-start gap-2">
                        <Settings className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Panel de Control:</strong> Todos los parámetros visibles</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Coins className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Sliders en Vivo:</strong> Certificados, precios, años</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Percent className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Reinversión:</strong> Ajuste instantáneo 0-100%</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Table className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Evolución Detallada:</strong> Tabla año por año</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <PieChart className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Gráficos Dinámicos:</strong> Actualización en tiempo real</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <BarChart3 className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                        <span><strong>Métricas Clave:</strong> ROI, TIR, multiplicador</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-cyan-50 p-4 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Zap className="w-5 h-5 text-cyan-600" />
                      <span className="font-medium text-cyan-800">Exploración Rápida</span>
                    </div>
                    <div className="text-xs text-cyan-700">
                      Perfecta para comparar escenarios en segundos. Ajusta cualquier valor
                      y observa el impacto inmediato en todos los resultados.
                    </div>
                  </div>

                  <div className="bg-amber-50 p-4 rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Award className="w-5 h-5 text-amber-600" />
                      <span className="font-medium text-amber-800">Sin Reportes</span>
                    </div>
                    <div className="text-xs text-amber-700">
                      Enfocada en análisis rápido y comparación de escenarios.
                      No incluye generación de reportes.
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="text-sm text-gray-500">
                      ⏱️ 1-2 minutos
                    </div>
                    <div className="flex items-center gap-2 text-cyan-600 group-hover:text-cyan-700 transition-colors">
                      <span className="font-medium">Iniciar</span>
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-16"
        >
          <div className="bg-white rounded-3xl shadow-xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
              Comparación Detallada de Funcionalidades
            </h3>

            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-4 px-6 font-semibold text-gray-800">Funcionalidad</th>
                    <th className="text-center py-4 px-6 font-semibold text-blue-700">Completa</th>
                    <th className="text-center py-4 px-6 font-semibold text-green-700">Simplificada</th>
                    <th className="text-center py-4 px-6 font-semibold text-amber-700">Retiro</th>
                    <th className="text-center py-4 px-6 font-semibold text-cyan-700">Express</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {[
                    { feature: 'Configuración de Idioma y Moneda', full: true, simplified: true, retirement: false, express: false },
                    { feature: 'Módulo de Educación Financiera', full: true, simplified: false, retirement: false, express: false },
                    { feature: 'Configuración de Metas del Inversionista', full: true, simplified: true, retirement: true, express: false },
                    { feature: 'Comparación AFORE vs PPR vs Cosecha', full: false, simplified: false, retirement: true, express: false },
                    { feature: 'Animación Año por Año', full: false, simplified: false, retirement: true, express: false },
                    { feature: 'Protección Legal (Trust, Voluntad)', full: true, simplified: false, retirement: false, express: false },
                    { feature: 'Datos Completos del Cliente', full: true, simplified: true, retirement: false, express: false },
                    { feature: 'Configuración de Inversión', full: true, simplified: true, retirement: true, express: true },
                    { feature: 'Análisis de Contexto de Mercado', full: true, simplified: false, retirement: false, express: false },
                    { feature: 'Plan de Retiros Personalizado', full: true, simplified: true, retirement: true, express: false },
                    { feature: 'Análisis de Impacto Sostenible', full: true, simplified: false, retirement: false, express: false },
                    { feature: 'Resultados con Gráficos', full: true, simplified: true, retirement: true, express: true },
                    { feature: 'Tabla de Evolución Detallada', full: true, simplified: true, retirement: true, express: true },
                    { feature: 'Plan de Financiamiento', full: true, simplified: true, retirement: false, express: false },
                    { feature: 'Cálculo de Descuentos por Tasa', full: true, simplified: true, retirement: false, express: false },
                    { feature: 'Reportes HTML Especializados', full: '5 reportes', simplified: '5 reportes', retirement: 'No', express: 'No' },
                    { feature: 'Exportación a PDF', full: true, simplified: true, retirement: false, express: false },
                    { feature: 'Ajustes en Tiempo Real', full: false, simplified: false, retirement: true, express: true },
                    { feature: 'Dashboard Interactivo', full: false, simplified: false, retirement: true, express: true },
                    { feature: 'Número de Pasos', full: '11 pasos', simplified: '7 pasos', retirement: '1 pantalla', express: '1 pantalla' },
                    { feature: 'Tiempo de Configuración', full: '15-20 min', simplified: '3-5 min', retirement: '5-7 min', express: '1-2 min' },
                    { feature: 'Ideal Para', full: 'Presentaciones', simplified: 'Videollamadas', retirement: 'Planeación Retiro', express: 'Exploración' }
                  ].map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="py-4 px-6 font-medium text-gray-800">{row.feature}</td>
                      <td className="py-4 px-6 text-center">
                        {typeof row.full === 'boolean' ? (
                          row.full ? (
                            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                              <span className="text-blue-600 font-bold">✓</span>
                            </div>
                          ) : (
                            <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                              <span className="text-gray-400">-</span>
                            </div>
                          )
                        ) : (
                          <span className="text-blue-700 font-medium">{row.full}</span>
                        )}
                      </td>
                      <td className="py-4 px-6 text-center">
                        {typeof row.simplified === 'boolean' ? (
                          row.simplified ? (
                            <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                              <span className="text-green-600 font-bold">✓</span>
                            </div>
                          ) : (
                            <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                              <span className="text-gray-400">-</span>
                            </div>
                          )
                        ) : (
                          <span className="text-green-700 font-medium">{row.simplified}</span>
                        )}
                      </td>
                      <td className="py-4 px-6 text-center">
                        {typeof row.retirement === 'boolean' ? (
                          row.retirement ? (
                            <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
                              <span className="text-amber-600 font-bold">✓</span>
                            </div>
                          ) : (
                            <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                              <span className="text-gray-400">-</span>
                            </div>
                          )
                        ) : (
                          <span className="text-amber-700 font-medium">{row.retirement}</span>
                        )}
                      </td>
                      <td className="py-4 px-6 text-center">
                        {typeof row.express === 'boolean' ? (
                          row.express ? (
                            <div className="w-6 h-6 bg-cyan-100 rounded-full flex items-center justify-center mx-auto">
                              <span className="text-cyan-600 font-bold">✓</span>
                            </div>
                          ) : (
                            <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                              <span className="text-gray-400">-</span>
                            </div>
                          )
                        ) : (
                          <span className="text-cyan-700 font-medium">{row.express}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-16 text-center"
        >
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-3xl p-8 text-white">
            <h3 className="text-3xl font-bold mb-4">
              Transforma tu Futuro Financiero Hoy
            </h3>
            <p className="text-green-100 mb-8 max-w-2xl mx-auto text-lg">
              El Interés Compuesto Multiplicador™ es más que una inversión:
              es la evolución del modelo tradicional que multiplica activos reales productivos
              mientras genera ingresos pasivos dolarizados.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setSelectedCalculator('full')}
                className="px-8 py-4 bg-white text-green-600 font-semibold rounded-2xl hover:bg-gray-50 transition-all duration-300 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl"
              >
                <Calculator className="w-6 h-6" />
                <span>Experiencia Completa</span>
              </button>

              <button
                onClick={() => setSelectedCalculator('simplified')}
                className="px-8 py-4 bg-green-400 text-white font-semibold rounded-2xl hover:bg-green-300 transition-all duration-300 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl"
              >
                <Video className="w-6 h-6" />
                <span>Consulta Rápida</span>
              </button>

              <button
                onClick={() => setSelectedCalculator('express')}
                className="px-8 py-4 bg-cyan-400 text-white font-semibold rounded-2xl hover:bg-cyan-300 transition-all duration-300 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl"
              >
                <Zap className="w-6 h-6" />
                <span>Exploración Express</span>
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default HomePage;
